/**
 * Fias Roles Management — members of one role.
 *
 * Directly addressable by role slug so an ops URL can be pasted into an incident
 * thread, matching the Providers section's rationale.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, ShieldAlert, UserMinus, UserPlus } from 'lucide-react';
import { api, type AdminRoleMember, type AdminRoleSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RoleMutationDialog, type RoleMutationMode } from './RoleMutationDialog';

function MfaBadge({ state }: { state: string | null }) {
  // Operational status only — never methods, secrets or recovery detail.
  if (state === 'enabled') return <Badge variant="secondary">MFA on</Badge>;
  return (
    <Badge variant="outline" className="text-muted-foreground">
      MFA off
    </Badge>
  );
}

export function RoleDetailPage() {
  const { role = '' } = useParams<{ role: string }>();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [dialog, setDialog] = useState<{
    mode: RoleMutationMode;
    presetUser?: { userId: string; username: string | null };
  } | null>(null);

  const catalogue = useQuery({
    queryKey: queryKeys.adminRoles.catalogue(),
    queryFn: () => api.adminGetRoles(),
  });

  // useInfiniteQuery, not useQuery-with-a-cursor. Paging by swapping a cursor
  // into the query key made every page its own cache entry and rendered only
  // the latest one, so "Load more" DELETED the first 50 members from the view.
  // On a large role that silently turns recertification into a one-page
  // exercise — an operator filters, sees nothing, and concludes the user does
  // not hold the role.
  const members = useInfiniteQuery({
    queryKey: queryKeys.adminRoles.members(role),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.adminGetRoleMembers(role, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: role.length > 0,
  });

  /** Every page loaded so far, flattened — the roster the operator is reading. */
  const allMembers = useMemo(
    () => members.data?.pages.flatMap((p) => p.members) ?? [],
    [members.data],
  );
  /** Drift is cumulative across pages, so the banner must sum, not sample. */
  const staleIndexEntries = useMemo(
    () => members.data?.pages.reduce((n, p) => n + (p.staleIndexEntries ?? 0), 0) ?? 0,
    [members.data],
  );
  const indexAvailable = members.data?.pages[0]?.indexAvailable;

  const summary: AdminRoleSummary | undefined = catalogue.data?.roles.find((r) => r.role === role);

  const visible = useMemo(() => {
    const rows = allMembers;
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((m) =>
      [m.username, m.displayName, m.userId, m.grantedBy, m.reason]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [allMembers, filter]);

  const refresh = () => {
    // Counts on the catalogue move too, so invalidate the whole namespace.
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminRoles.all });
  };

  if (catalogue.isSuccess && !summary) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-8">
        <Link
          to="/dev-console/roles"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Back to Fias Roles
        </Link>
        <p className="mt-6 text-sm text-destructive">
          There is no role called <code className="font-mono">{role}</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      <Link
        to="/dev-console/roles"
        className="mb-6 inline-block text-sm text-muted-foreground hover:text-foreground"
      >
        ← Back to Fias Roles
      </Link>

      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-foreground">{summary?.label ?? role}</h1>
        {summary?.readOnly && (
          <Badge variant="outline" className="gap-1">
            <Lock className="h-3 w-3" aria-hidden="true" />
            Read-only
          </Badge>
        )}
        {summary?.reserved && <Badge variant="secondary">Reserved — not enforced</Badge>}
      </div>
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{summary?.description}</p>

      {summary?.role === 'admin' && (
        <p className="mb-6 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Administrators cannot be added or removed from this tool — that is deliberate. Review
            this list regularly and investigate any name you do not expect.
          </span>
        </p>
      )}

      {indexAvailable === false && (
        <p className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
            aria-hidden="true"
          />
          <span>
            The role membership index is not available yet, so members cannot be listed. This does
            NOT mean nobody holds this role — deploy the RoleIndex GSI and run the backfill.
          </span>
        </p>
      )}

      {staleIndexEntries > 0 && (
        <p className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
            aria-hidden="true"
          />
          <span>
            {staleIndexEntries} index{' '}
            {staleIndexEntries === 1 ? 'entry' : 'entries'} no longer matched a user
            profile and {staleIndexEntries === 1 ? 'was' : 'were'} hidden. This
            usually means a role was changed directly in the database rather than through this tool.
          </span>
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter members…"
          className="max-w-xs"
          aria-label="Filter members"
        />
        {summary?.managed && (
          <Button
            type="button"
            className="ml-auto gap-1.5"
            onClick={() => setDialog({ mode: 'grant' })}
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            Grant {summary.label}
          </Button>
        )}
      </div>

      {members.isLoading && <p className="text-sm text-muted-foreground">Loading members…</p>}
      {members.isError && (
        <p className="text-sm text-destructive">
          {members.error instanceof Error ? members.error.message : 'Failed to load members.'}
        </p>
      )}

      {members.data && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Granted</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                {summary?.managed && <th className="px-3 py-2 font-medium sr-only">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {filter ? 'No members match that filter.' : 'Nobody holds this role.'}
                  </td>
                </tr>
              )}
              {visible.map((m: AdminRoleMember) => (
                <tr key={m.userId} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{m.username ?? '—'}</div>
                    {m.displayName && (
                      <div className="text-xs text-muted-foreground">{m.displayName}</div>
                    )}
                    <div className="font-mono text-xs text-muted-foreground">{m.userId}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={m.status === 'active' ? 'secondary' : 'destructive'}>
                        {m.status ?? 'unknown'}
                      </Badge>
                      <MfaBadge state={m.mfaState} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {m.grantedAt ? new Date(m.grantedAt).toLocaleDateString() : '—'}
                    {m.grantedBy && (
                      <div className="font-mono text-xs">
                        {m.grantedBy === 'historical-backfill' ? 'imported' : m.grantedBy}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[16rem] px-3 py-2 text-muted-foreground">
                    {m.reason ?? '—'}
                  </td>
                  {summary?.managed && (
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-destructive"
                        onClick={() =>
                          setDialog({
                            mode: 'revoke',
                            presetUser: { userId: m.userId, username: m.username },
                          })
                        }
                      >
                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                        Revoke
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {members.hasNextPage && (
        <Button
          type="button"
          variant="secondary"
          className="mt-4"
          disabled={members.isFetchingNextPage}
          onClick={() => void members.fetchNextPage()}
        >
          {members.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}

      {/*
        Wording matters. A grant is inert until the target's token is reminted,
        and a revoke does not sign anyone out — it stops their existing token
        authorizing privileged operations at its next check.
      */}
      {summary?.managed && (
        <p className="mt-4 text-xs text-muted-foreground">
          Granting a role takes effect the next time the user signs in. Revoking stops their
          existing sessions authorizing this role at the next privileged request.
        </p>
      )}

      {dialog && summary && (
        <RoleMutationDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          mode={dialog.mode}
          role={role}
          roleLabel={summary.label}
          presetUser={dialog.presetUser}
          onSuccess={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
